/**
 * Highlighting for the PDF text layer (#627).
 *
 * react-pdf hands each text item back as an **HTML string**, so the search
 * cannot simply wrap a match: the item text has to be escaped first, and the
 * term has to be escaped the same way to still match it. Doing this the other
 * way round lets a document containing `&amp;` or `<` either lose characters or
 * inject markup.
 */

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const escapeHtml = (value: string) =>
  value.replace(/[&<>]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[char] as string)

/**
 * The item's text as HTML, with every case-insensitive occurrence of `term`
 * wrapped in a `mark`. An empty term returns the escaped text unchanged.
 */
export function highlightTextItem(str: string, term: string): string {
  const safe = escapeHtml(str)
  const needle = term.trim()
  if (!needle) return safe
  return safe.replace(
    new RegExp(escapeRegExp(escapeHtml(needle)), 'gi'),
    (hit) => `<mark class="pdf-search-hit">${hit}</mark>`,
  )
}

/** Case-insensitive occurrences of `term` in `text`. 0 for an empty term. */
export function countMatches(text: string, term: string): number {
  const needle = term.trim()
  if (!needle) return 0
  return text.match(new RegExp(escapeRegExp(needle), 'gi'))?.length ?? 0
}
